/**
 * Maintenance-window fixtures — the port of the DOORS, not of the raise.
 *
 * The server (ALM-05, G-ALM-05) keeps a `acs_maintenance` row per planned
 * quiet night and answers three doors: list (`alarm.read`), create and
 * cancel (`alarm.ack` at edit, human-only, Idempotency-Key). This file ports
 * the rules those doors enforce so the dev console and the real façade tell
 * the operator the same story:
 *
 *  - `MAINTENANCE_SCOPES` / `MAINTENANCE_STATES` are `models.py`'s own
 *    alphabets (`:558` / `:565`) — no `fleet` scope, no `expired` state.
 *  - a `device` target must name a row the fixture owns (the server checks
 *    `get_device`); an `olt` target is free text, because the façade keeps
 *    no OLT inventory and a typo is a window that never matches, not an
 *    error it can raise;
 *  - the interval is the SAME one-piece ISO-UTC shape the campaign door
 *    reads, so the fixture reuses `parseWindow` from `campaignEngine.ts`
 *    rather than growing a second reader of the same grammar;
 *  - cancel is CAS: the second canceller hears `already_cancelled` and the
 *    FIRST canceller's name stays on the row.
 *
 * The named divergence (plan D4): the alarm feed here does NOT consult the
 * windows. Suppression lives at the raise on the server; this fixture has no
 * live raise — its event day is static — so `suppressed` stays a stored fact
 * born 0, and a counter derived from a read would be the same lie V4 refused.
 */

import type {
  MaintenanceCreateRequest,
  MaintenanceScope,
  MaintenanceState,
  MaintenanceWindow,
} from '../api/types';
import { FIXTURE_DEVICES } from './devices';
import { parseWindow } from './campaignEngine';

/** `MAINTENANCE_SCOPES` in `acs_service/models.py:558`. */
export const MAINTENANCE_SCOPES: readonly MaintenanceScope[] = ['device', 'olt'];

/** `MAINTENANCE_STATES` in `acs_service/models.py:565`. */
export const MAINTENANCE_STATES: readonly MaintenanceState[] = ['active', 'cancelled'];

/** `MaintenanceListResponse`'s limit ceiling (`Query(..., le=200)` on the door). */
export const MAINTENANCE_PAGE_SIZE = 200;

/**
 * The named human the fixture records as `created_by` / `cancelled_by`. The
 * real façade refuses a machine assertion and names whoever signed in; the
 * fixture's console session is that stand-in (same role `FIXTURE_ACK_CLIENT`
 * plays for the ack ledger).
 */
export const MAINTENANCE_WINDOW_PLANNER = 'user:console-owner';

/** The door's 404 wording, verbatim (`routers/alarms.py::cancel_maintenance`). */
export function unknownWindowDetail(windowId: string): string {
  return `no maintenance window ${windowId} is known to the ACS`;
}

/** The create door's 404 wording for a device target the store does not own. */
export function unknownDeviceDetail(target: string): string {
  return `no device ${target} is known to the ACS`;
}

/** The create door's alphabet lines, verbatim where the server writes them. */
export const SCOPE_DETAIL = `scope must be one of ${MAINTENANCE_SCOPES.join(', ')}`;
export const STATE_DETAIL = `state must be one of ${MAINTENANCE_STATES.join(', ')}`;

/** One seeded window row, birthed with every contract key present. */
function row(
  windowId: string,
  scope: MaintenanceScope,
  target: string,
  startsAt: string,
  endsAt: string,
  note: string | null,
  state: MaintenanceState,
  createdAt: string,
  cancelledAt: string | null = null,
  cancelledBy: string | null = null,
): MaintenanceWindow {
  return {
    window_id: windowId,
    scope,
    target,
    starts_at: startsAt,
    ends_at: endsAt,
    note,
    state,
    created_at: createdAt,
    created_by: MAINTENANCE_WINDOW_PLANNER,
    cancelled_at: cancelledAt,
    cancelled_by: cancelledBy,
    suppressed: 0,
  };
}

/**
 * The dev night, as data: one plan still standing for the noisiest device on
 * the fixture fleet, and one already-cancelled OLT window whose row keeps
 * the canceller's name — the two lifecycle stories the panel has to render.
 */
export const FIXTURE_MAINTENANCE_WINDOWS: readonly MaintenanceWindow[] = [
  row(
    'mwin:fixture-active-device',
    'device',
    FIXTURE_DEVICES[0]!.acs_device_id,
    '2026-10-14T01:00:00Z',
    '2026-10-14T05:00:00Z',
    'تعمیر فیبر پنل B — شبِ بی‌آلارم برنامه‌ریزی‌شده',
    'active',
    '2026-10-13T09:30:00Z',
  ),
  row(
    'mwin:fixture-cancelled-olt',
    'olt',
    'OLT-1',
    '2026-10-12T02:00:00Z',
    '2026-10-12T06:00:00Z',
    'آپگرید نرم‌افزار OLT — لغو شد چون پنجرهٔ پشتیبانی گذشت',
    'cancelled',
    '2026-10-11T15:00:00Z',
    '2026-10-11T18:45:00Z',
    MAINTENANCE_WINDOW_PLANNER,
  ),
];

/** `created_at` desc, `window_id` asc — the store's own order (`store.py:1260`). */
export function sortWindowRows(rows: readonly MaintenanceWindow[]): MaintenanceWindow[] {
  return [...rows].sort(
    (a, b) => b.created_at.localeCompare(a.created_at) || a.window_id.localeCompare(b.window_id),
  );
}

/**
 * The create door's validation pass, shared by the fixture and the page's
 * early check: every refusal carries the server's own code and wording. The
 * device miss is the door's 404 `device-not-found`; everything else is 422
 * `reference-invalid`. Throws nothing — the caller maps the answer.
 */
export function validateWindowCreate(
  body: MaintenanceCreateRequest,
  deviceExists: (target: string) => boolean,
):
  | { startsAt: string; endsAt: string }
  | { why: string; code: 'device-not-found' | 'reference-invalid' } {
  if (!MAINTENANCE_SCOPES.includes(body.scope)) {
    return { why: SCOPE_DETAIL, code: 'reference-invalid' };
  }
  if (!body.target?.trim()) {
    return { why: 'target must not be empty', code: 'reference-invalid' };
  }
  if (body.note !== undefined && body.note !== null && body.note.length > 500) {
    return { why: 'note must be at most 500 characters', code: 'reference-invalid' };
  }
  // `parseWindow` RETURNS null only for a blank window; every unreadable
  // shape it THROWS on must land in the same 422 answer, not escape raw.
  let parsed: [Date, Date] | null = null;
  try {
    parsed = parseWindow(body.window);
  } catch (exc) {
    return { why: (exc as Error).message, code: 'reference-invalid' };
  }
  if (!parsed) {
    return {
      why: 'a maintenance window is exactly one interval',
      code: 'reference-invalid',
    };
  }
  if (body.scope === 'device' && !deviceExists(body.target)) {
    return { why: unknownDeviceDetail(body.target), code: 'device-not-found' };
  }
  return { startsAt: isoSeconds(parsed[0]), endsAt: isoSeconds(parsed[1]) };
}

/** The row's stamp shape: ISO seconds, `Z` (the door's `.isoformat` line). */
export function isoSeconds(date: Date): string {
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
}
